import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './commercial-description.css';

/**
 * ExpandableText - Truncates text with a "View more +" toggle on mobile.
 * Shows full content on desktop. Only shows toggle when content overflows.
 */
const ExpandableText = ({ html, maxHeight = 238 }) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollHeight > maxHeight);
    }
  }, [html, maxHeight]);

  return (
    <div className="commercial-description__expandable">
      <div
        ref={textRef}
        className={`commercial-description__text ${!expanded && isOverflowing ? 'commercial-description__text--clamped' : ''}`}
        style={!expanded && isOverflowing ? { maxHeight: `${maxHeight}px` } : undefined}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {isOverflowing && (
        <button
          type="button"
          className="commercial-description__view-more"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'View less −' : 'View more +'}
        </button>
      )}
    </div>
  );
};

/**
 * PropertyDescription - Renders the Property Description and Investment Overview
 * sections for the Commercial PDP. Reads from Redux pdpInitData.
 */
const PropertyDescription = () => {
  const pdpInitData = useSelector((state) => state.pdp?.pdpInitData);
  const propertyInfo = pdpInitData?.propertyDetails?.propertyInformation;
  const commercialFeatures = pdpInitData?.propertySpecialDetails?.commercialPropertyAdditionalFeatures;
  const description = commercialFeatures?.marketingDescription;

  const investmentOverview = commercialFeatures?.propertyInformation;

  if (!description && !propertyInfo && !investmentOverview) return null;

  return (
    <div className="commercial-description">
      {/* Property Description */}
      {(description || propertyInfo) && (
        <div className="commercial-description__section">
          <h2 className="commercial-description__heading">Property Description</h2>
          <ExpandableText html={description || propertyInfo} />
        </div>
      )}

      {/* Investment Overview / Executive Summary */}
      {investmentOverview && (
        <div className="commercial-description__section">
          <h2 className="commercial-description__heading">Executive Summary</h2>
          <ExpandableText html={investmentOverview} maxHeight={390} />
        </div>
      )}
    </div>
  );
};

export default PropertyDescription;
